export interface ResolveEditRecipeImageForSubmitParams {
  currentImageUrl: string | null | undefined;
  selectedImageFile: File | null;
  resizeImage: (file: File, maxWidth: number, maxHeight: number, quality: number) => Promise<File>;
  uploadImage: (file: File) => Promise<string>;
  triggerPreviousImageDeletion: (imageUrl: string, errorLabel: string) => void;
}

export interface ResolveEditRecipeImageForSubmitResult {
  imageUrl: string | null;
  shouldAbort: boolean;
  errorMessage?: string;
}

export interface DeleteEditRecipeImageParams {
  recipeId: string;
  previewImage: string;
  clearRecipeImage: (recipeId: string, imageUrl: string, errorLabel: string) => Promise<void>;
}

export interface DeleteEditRecipeImageResult {
  success: boolean;
  errorMessage?: string;
}

interface TriggerEditRecipeImageGenerationParams {
  recipeId: string;
  token: string | null | undefined;
  imageUrl: string | null;
  triggerRecipeImageGeneration: (
    recipeId: string,
    token: string,
    options: {
      requestErrorLabel: string;
      responseErrorLabel: string;
    }
  ) => void;
}

export const getEditRecipeImageErrorMessage = (error: unknown) => (
  error instanceof Error ? error.message : 'Erreur inconnue'
);

export const getRecipeImageOrNull = (image: string | null | undefined) => image || null;

export const shouldSubscribeToEditRecipeImageRealtime = (
  recipeImage: string | null | undefined,
  selectedImageFile: File | null
) => !recipeImage && !selectedImageFile;

export const resolveRealtimeEditRecipePreviewImage = (
  incomingImage: string | null,
  currentPreviewImage: string | null
) => (
  incomingImage && incomingImage !== currentPreviewImage
    ? incomingImage
    : null
);

export const shouldSkipEditRecipeImageDelete = ({
  isAuthor,
  token,
  previewImage,
  confirmDelete,
}: {
  isAuthor: boolean;
  token: string | null | undefined;
  previewImage: string | null;
  confirmDelete: () => boolean;
}) => !isAuthor || !token || !previewImage || !confirmDelete();

export const resolveEditRecipeImageForSubmit = async ({
  currentImageUrl,
  selectedImageFile,
  resizeImage,
  uploadImage,
  triggerPreviousImageDeletion,
}: ResolveEditRecipeImageForSubmitParams): Promise<ResolveEditRecipeImageForSubmitResult> => {
  if (!selectedImageFile) {
    return { imageUrl: currentImageUrl || null, shouldAbort: false };
  }

  if (currentImageUrl) {
    triggerPreviousImageDeletion(
      currentImageUrl,
      'Could not extract old file path from recipe image:'
    );
  }

  try {
    const resizedFile = await resizeImage(selectedImageFile, 1024, 1024, 0.7);
    const imageUrl = await uploadImage(resizedFile);

    return { imageUrl, shouldAbort: false };
  } catch (error: unknown) {
    return {
      imageUrl: currentImageUrl || null,
      shouldAbort: true,
      errorMessage: `Erreur lors de l'upload de l'image: ${getEditRecipeImageErrorMessage(error)}`,
    };
  }
};

export const deleteEditRecipeImage = async ({
  recipeId,
  previewImage,
  clearRecipeImage,
}: DeleteEditRecipeImageParams): Promise<DeleteEditRecipeImageResult> => {
  try {
    await clearRecipeImage(
      recipeId,
      previewImage,
      'Could not extract file path from current recipe image URL:'
    );

    return { success: true };
  } catch (error: unknown) {
    return {
      success: false,
      errorMessage: `Erreur lors de la suppression de l'image : ${getEditRecipeImageErrorMessage(error)}`,
    };
  }
};

export const triggerEditRecipeImageGenerationIfNeeded = ({
  recipeId,
  token,
  imageUrl,
  triggerRecipeImageGeneration,
}: TriggerEditRecipeImageGenerationParams) => {
  if (imageUrl || !token) {
    return;
  }

  triggerRecipeImageGeneration(recipeId, token, {
    requestErrorLabel: 'Erreur lors du trigger de génération IA:',
    responseErrorLabel: 'Erreur Netlify lors de la génération IA:',
  });
};
